import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

/**
 * Composable to check if the current user can manage specific teams or inboxes
 * based on custom role permissions
 * @returns {Object} - Functions to check permissions for teams and inboxes
 */
export function useSettingsPermissions() {
  const getters = useStoreGetters();
  const { accountId } = useAccount();

  const currentUser = computed(() => getters.getCurrentUser.value);
  const currentAccount = computed(() => {
    if (!currentUser.value || !accountId.value) return null;
    return currentUser.value.accounts?.find(
      acc => Number(acc.id) === Number(accountId.value)
    );
  });

  const userPermissions = computed(() => {
    return currentAccount.value?.permissions || [];
  });

  const customRole = computed(() => {
    return currentAccount.value?.custom_role;
  });

  /**
   * Check if user can edit a specific team
   * @param {Number} teamId - The team ID to check
   * @returns {Boolean} - True if user can edit the team
   */
  const canEditTeam = teamId => {
    // Administrators can always edit, unless they have a custom role assigned
    if (currentAccount.value?.role === 'administrator' && !customRole.value)
      return true;

    // Check if user has settings_teams_manage permission
    if (!userPermissions.value.includes('settings_teams_manage')) return false;

    // If custom role exists and has allowed_team_ids, check if team is in the list
    if (customRole.value?.allowed_team_ids) {
      const allowedIds = customRole.value.allowed_team_ids;
      // If array is empty, user can edit all teams
      if (allowedIds.length === 0) return true;
      // Otherwise, check if team is in the allowed list
      return allowedIds.includes(Number(teamId));
    }

    // If no custom role restrictions, user can edit
    return true;
  };

  /**
   * Check if user can edit a specific inbox
   * @param {Number} inboxId - The inbox ID to check
   * @returns {Boolean} - True if user can edit the inbox
   */
  const canEditInbox = inboxId => {
    // Administrators can always edit, unless they have a custom role assigned
    if (currentAccount.value?.role === 'administrator' && !customRole.value)
      return true;

    // Check if user has settings_inboxes_manage permission
    if (!userPermissions.value.includes('settings_inboxes_manage'))
      return false;

    // If custom role exists and has allowed_inbox_ids, check if inbox is in the list
    if (customRole.value?.allowed_inbox_ids) {
      const allowedIds = customRole.value.allowed_inbox_ids;
      // If array is empty, user can edit all inboxes
      if (allowedIds.length === 0) return true;
      // Otherwise, check if inbox is in the allowed list
      return allowedIds.includes(Number(inboxId));
    }

    // If no custom role restrictions, user can edit
    return true;
  };

  /**
   * Check if user can configure a specific team
   * @param {Number} teamId - The team ID to check
   * @returns {Boolean} - True if user can configure the team
   */
  const canConfigureTeam = teamId => {
    // Administrators can always configure, unless they have a custom role assigned
    if (currentAccount.value?.role === 'administrator' && !customRole.value)
      return true;

    // Check if user has settings_teams_manage permission
    if (!userPermissions.value.includes('settings_teams_manage')) return false;

    const allowedIds = customRole.value?.allowed_team_ids || [];
    const configurableIds = customRole.value?.configurable_team_ids || [];

    // Check if there are access restrictions
    const hasAccessRestrictions = allowedIds.length > 0;

    // First check if user has access to view this team
    if (hasAccessRestrictions && !allowedIds.includes(Number(teamId))) {
      return false;
    }

    // Check configuration permissions
    // If there are access restrictions but no specific configurable IDs, user cannot configure
    if (hasAccessRestrictions && configurableIds.length === 0) {
      return false;
    }

    // If no access restrictions and no configurable restrictions, user can configure all
    if (configurableIds.length === 0) return true;

    // If there are specific configurable IDs, check if team is in the list
    return configurableIds.includes(Number(teamId));
  };

  /**
   * Check if user can configure a specific inbox
   * @param {Number} inboxId - The inbox ID to check
   * @returns {Boolean} - True if user can configure the inbox
   */
  const canConfigureInbox = inboxId => {
    // Administrators can always configure, unless they have a custom role assigned
    if (currentAccount.value?.role === 'administrator' && !customRole.value)
      return true;

    // Check if user has settings_inboxes_manage permission
    if (!userPermissions.value.includes('settings_inboxes_manage'))
      return false;

    const allowedIds = customRole.value?.allowed_inbox_ids || [];
    const configurableIds = customRole.value?.configurable_inbox_ids || [];

    // Check if there are access restrictions
    const hasAccessRestrictions = allowedIds.length > 0;

    // First check if user has access to view this inbox
    if (hasAccessRestrictions && !allowedIds.includes(Number(inboxId))) {
      return false;
    }

    // Check configuration permissions
    // If there are access restrictions but no specific configurable IDs, user cannot configure
    if (hasAccessRestrictions && configurableIds.length === 0) {
      return false;
    }

    // If no access restrictions and no configurable restrictions, user can configure all
    if (configurableIds.length === 0) return true;

    // If there are specific configurable IDs, check if inbox is in the list
    return configurableIds.includes(Number(inboxId));
  };

  return {
    canEditTeam,
    canEditInbox,
    canConfigureTeam,
    canConfigureInbox,
  };
}
